import React, { useState, useEffect, KeyboardEventHandler } from "react";
import { getAxios, route } from "../../utils/endpoints";
import { DropdownValue } from "../../types/DropdownValue";
import { SelectChangeEvent } from "@mui/material";
import { LeadInfo } from "../../types/LeadInfo";

type IState = {
  firstname: string;
  lastname: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  email: string;
  email2: string;
  product: string;
  status: string;
  apptDt: Date | null;
  time: string;
  notes: string;
};

type SnackbarAction = {
  label: string;
  onClick: () => void;
};

type SnackbarState = {
  open: boolean;
  title: string;
  level: string;
  action: SnackbarAction | undefined;
};

type PossibleMatch = {
  match: null | any;
  id: number;
  callerAni: string;
};

const initialSnackbarState = {
  open: false,
  title: "",
  level: "info",
  action: undefined,
};

const initialState: IState = {
  address: "",
  apptDt: null,
  city: "",
  email: "",
  email2: "",
  firstname: "",
  lastname: "",
  product: "",
  state: "",
  status: "",
  time: "",
  zip: "",
  notes: "",
};

type LeadStatus = {
  id: string;
  description: string;
};

type CustomFields = {
  fieldId: number;
  fieldName: string;
  fieldType: "S" | "D";
  required: boolean;
  selectedValue: string;
  dropdownValues: DropdownValue[];
};

export const useAddLead = () => {
  const [typingTimer, setTypingTimer] = useState<any>();
  const [leadStatuses, setLeadStatuses] = useState<LeadStatus[]>([]);
  const [leadState, setLeadState] = useState<IState>(initialState);
  const [dealerMsg, setDealerMsg] = useState<string>("");
  const [dealerMsgColor, setDealerMsgColor] = useState<string>("");
  const [softMsgColor, setSoftMsgColor] = useState<string>("");
  const [possibleMatches, setPossibleMatches] = useState<PossibleMatch[]>([]);
  const [dealer, setDealer] = useState<string>("");
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([""]);
  const [isDealerInfoDialogOpen, setIsDealerInfoDialogOpen] =
    useState<boolean>(false);
  const [isPossibleMatchesDialogOpen, setIsPossibleMatchesDialogOpen] =
    useState<boolean>(false);
  const [snackbarState, setSnackbarState] =
    useState<SnackbarState>(initialSnackbarState);
  const [dealerSources, setDealerSources] = useState<DropdownValue[]>([]);
  const [source, setSource] = useState<DropdownValue>({
    description: "",
    id: 0,
    sortOrder: 0,
  });

  const [products, setProducts] = useState<DropdownValue[]>([]);
  const [dropdownApptTimeRange, setDropdownApptTimeRange] = useState<
    DropdownValue[]
  >([]);
  const [formFields, setFormFields] = useState<CustomFields[]>([]);

  const addPhoneNumber = (): void => {
    if (phoneNumbers.length >= 3) {
      return;
    }

    setPhoneNumbers((prevState) => [...prevState, ""]);
  };

  const deletePhoneNumber = (): void => {
    if (phoneNumbers.length <= 1) {
      return;
    }

    const arr = phoneNumbers.slice(0, -1);

    setPhoneNumbers(arr);
  };

  const onHandlePhonenumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const values = [...phoneNumbers];

    values[parseInt(event.target.name)] = event.target.value;

    setPhoneNumbers(values);
  };

  const handleDealerInfoOpen = () => setIsDealerInfoDialogOpen(true);
  const handleDealerInfoClose = () => setIsDealerInfoDialogOpen(false);

  const handlePossibleMatchesOpen = () => setIsPossibleMatchesDialogOpen(true);
  const handlePossibleMatchesClose = () =>
    setIsPossibleMatchesDialogOpen(false);

  const handleSnackbarClose = () => setSnackbarState(initialSnackbarState);

  const onChangeDealer = (event: SelectChangeEvent<unknown>) => {
    setDealer(event.target.value as string);
  };

  const onChangeSelect = (event: SelectChangeEvent<unknown>) => {
    setLeadState((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value as string,
    }));
  };

  const onChangeDate = (newDate: Date) => {
    setLeadState((prevState) => ({
      ...prevState,
      apptDt: newDate,
    }));
  };

  const onChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setLeadState((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));

    clearTimeout(typingTimer);

    if (event.target.value) {
      setTypingTimer(setTimeout(getPossibleMatches, 1000));
    }
  };

  const onChangeAutocomplete = (
    event: React.SyntheticEvent<Element, Event>,
    value: DropdownValue | null,
    name: string,
  ) => {
    setSource(value!);
  };

  const onChangeFormField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const updatedFormFields = [...formFields];
    updatedFormFields[index] = {
      ...updatedFormFields[index],
      selectedValue: event.target.value,
    };
    setFormFields(updatedFormFields);
  };

  const onChangeFormFieldSelect = (
    event: SelectChangeEvent<unknown>,
    index: number,
  ) => {
    const updatedFormFields = [...formFields];
    updatedFormFields[index] = {
      ...updatedFormFields[index],
      selectedValue: event.target.value as string,
    };
    setFormFields(updatedFormFields);
  };

  const onFetchDealerSource = async () => {
    const numberResponse = await getAxios(route.apiOptions.subcampaign, "GET");

    const data: DropdownValue[] = await numberResponse.data;

    setDealerSources(data);
  };

  const onFetchDealerMsg = async (): Promise<void> => {
    const response = await getAxios(`/dealers/${dealer}/info`, "GET");

    const data: LeadInfo = await response.data;

    setDealerMsg(data.dealerMsg);

    switch (data.dealerMsgClass) {
      case "msgBlue": {
        setDealerMsgColor("#1BA0E9");
        setSoftMsgColor("lightblue");
        break;
      }

      case "msgRed": {
        setDealerMsgColor("red");
        setSoftMsgColor("lightred");
        break;
      }

      case "msgGreen": {
        setDealerMsgColor("green");
        setSoftMsgColor("lightgreen");
        break;
      }
    }
  };

  const onFetchProducts = async (): Promise<void> => {
    const response = await getAxios("/options/product", "GET");

    const data: DropdownValue[] = await response.data;

    setProducts(data);
  };

  const onFetchApptTimes = async (): Promise<void> => {
    const response = await getAxios("/options/appt-times", "GET");

    const data: DropdownValue[] = await response.data;

    setDropdownApptTimeRange(data);
  };

  const isValidForm = (): boolean => {
    const requiredFields = ["zip", "status"];

    if (!dealer) {
      setSnackbarState({
        open: true,
        title: "Dealer cannot be empty",
        action: undefined,
        level: "error",
      });

      return false;
    }

    if (!source.description) {
      setSnackbarState({
        open: true,
        title: "Lead Source cannot be empty",
        action: undefined,
        level: "error",
      });

      return false;
    }

    const isPhoneValid = phoneNumbers.every((val, index) => {
      if (!val) {
        setSnackbarState({
          open: true,
          title: `Phone Number cannot be empty`,
          action: undefined,
          level: "error",
        });
      }

      return val ? true : false;
    });

    if (!isPhoneValid) {
      return false;
    }

    let isValid: boolean = true;

    for (const key in leadState) {
      const objKey = key as keyof typeof leadState;

      if (requiredFields.includes(key)) {
        if (!leadState[objKey]) {
          const keyUppercase: string = key[0].toUpperCase() + key.slice(1);

          setSnackbarState({
            open: true,
            title: `${keyUppercase} cannot be empty`,
            action: undefined,
            level: "error",
          });

          isValid = false;
        }
      }
    }

    return isValid;
  };

  const callDetails = async () => {
    const response = await getAxios("/call-detail/additional-fields", "GET");

    const data: CustomFields[] = await response.data;

    setFormFields(data);
  };

  const onAddLead = async (): Promise<void> => {
    let isValid: boolean = true;

    formFields
      .filter((field) => field.required)
      .forEach((field) => {
        if (field.selectedValue === "" || field.selectedValue === null) {
          isValid = false;
        }
      });

    if (!isValid) {
      setSnackbarState((prevState) => ({
        open: true,
        title: "Please fill in all the required fields",
        action: undefined,
        level: "error",
      }));

      return;
    }

    if (!isValidForm()) {
      return;
    }

    try {
      const appDtMonth =
        leadState.apptDt !== null
          ? ("0" + (leadState.apptDt!.getMonth() + 1)).slice(-2)
          : "";
      const appDtDay =
        leadState.apptDt !== null
          ? ("0" + leadState.apptDt!.getDate()).slice(-2)
          : "";

      const response = await getAxios("/call-detail", "POST", {
        FirstName: leadState.firstname,
        LastName: leadState.lastname,
        Address: leadState.address,
        City: leadState.city,
        State: leadState.state,
        Zip: leadState.zip,
        Phone: phoneNumbers[0],
        Phone2: phoneNumbers[1],
        Phone3: phoneNumbers[2],
        Email: leadState.email,
        Emai2: leadState.email2,
        ProductId: leadState.product,
        Source: source.description,
        DealerId: dealer,
        ApptDate:
          leadState.apptDt !== null
            ? `${appDtMonth}-${appDtDay}-${leadState.apptDt!.getUTCFullYear()}`
            : "",
        ApptTime: leadState.time,
        Notes: leadState.notes,
        Status: leadState.status,
        UnassignedDealer: "",
        FirstName2: "",
        LastName2: "",
        jsonAdditionalFields: JSON.stringify(formFields),
      });

      setSnackbarState({
        open: true,
        title: "Lead has been created",
        action: undefined,
        level: "success",
      });

      setLeadState(initialState);
      setPhoneNumbers([""]);
      setSource({ description: "", id: 0, sortOrder: 0 });
      setDealer("");
    } catch (error) {
      setSnackbarState({
        open: true,
        title: "Something went wrong",
        action: undefined,
        level: "error",
      });

      console.error(error);
    }
  };

  const onFetchLeadStatus = async (): Promise<void> => {
    const response = await getAxios(route.apiOptions.leadStatus, "GET");

    const data: LeadStatus[] = await response.data;

    setLeadStatuses(data);
  };

  const getDealerInfo = async (): Promise<void> => {
    const response = await getAxios(`/dealers/find`, "GET", undefined, {
      product: leadState.product,
      zip: leadState.zip,
      source: source.description,
    });

    const data = await response.data;

    setDealer(data.dealerID);
  };

  const getPossibleMatches = async (): Promise<void> => {
    const response = await getAxios("/call-detail/matches", "GET", null, {
      firstName: leadState.firstname,
      lastName: leadState.lastname,
      address: leadState.address,
      city: leadState.city,
      state: leadState.state,
      zip: leadState.zip,
      phone: phoneNumbers[0],
      phone2: phoneNumbers[1],
      phone3: phoneNumbers[2],
      email: leadState.email,
      email2: leadState.email2,
    });

    const data = await response.data;

    if (data.length) {
      setPossibleMatches(data);

      setSnackbarState({
        level: "info",
        open: true,
        title: `We found ${data.length} possible matches`,
        action: { label: "See more", onClick: handlePossibleMatchesOpen },
      });
    }
  };

  React.useEffect(() => {
    onFetchProducts();
    onFetchApptTimes();
    onFetchDealerSource();
    onFetchLeadStatus();
    callDetails();
  }, []);

  React.useEffect(() => {
    onFetchDealerMsg();
  }, [dealer]);

  return {
    dealer,
    products,
    dropdownApptTimeRange,
    phoneNumbers,
    formFields,
    source,
    possibleMatches,
    snackbarState,
    isDealerInfoDialogOpen,
    leadStatuses,
    dealerSources,
    addPhoneNumber,
    deletePhoneNumber,
    onHandlePhonenumberChange,
    handleSnackbarClose,
    handleDealerInfoOpen,
    handleDealerInfoClose,
    handlePossibleMatchesOpen,
    handlePossibleMatchesClose,
    isPossibleMatchesDialogOpen,
    onChangeDealer,
    onChangeSelect,
    onChangeFormField,
    onChangeFormFieldSelect,
    onChangeAutocomplete,
    onChange,
    onFetchDealerMsg,
    dealerMsg,
    dealerMsgColor,
    softMsgColor,
    leadState,
    onAddLead,
    getDealerInfo,
    onChangeDate,
  };
};
